import { useCallback, useEffect, useRef, useState } from "react";
import type { RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import { listNotifications, markAllRead, markRead } from "../services/api";
import { setTokenProvider, subscribeToHub } from "../services/hub";
import type { Notification } from "../types";

export interface UseNotifications {
  notifications: Notification[];
  unreadCount: number;
  /** True once the live SignalR connection is established. */
  connected: boolean;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  /** Re-fetch the inbox from the API. */
  refresh: () => void;
}

const MAX_ITEMS = 100;

/**
 * Loads the notification inbox (offline catch-up) and subscribes to live
 * pushes over SignalR. Live deliveries and fetched rows are merged with
 * id-based dedup, so a push that races the initial fetch is shown once.
 *
 * The token is read through a ref so the SignalR connection isn't torn down
 * and rebuilt on every token refresh.
 */
export function useNotifications(
  token: string | null,
  refreshTokens?: RefreshTokenFn,
  onNotification?: (n: Notification) => void,
): UseNotifications {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);

  const tokenRef = useRef(token);
  tokenRef.current = token;
  const refreshRef = useRef(refreshTokens);
  refreshRef.current = refreshTokens;
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;
  // Coalesce concurrent inbox fetches: mount fires several refresh() sources at
  // once (initial-fetch effect + the hub's immediate onState + the connect
  // event, times any transient extra subscribers), and they'd otherwise each
  // hit the inbox API. While one load is in flight, others mark a single trailing
  // re-run instead of issuing their own request.
  const loadingRef = useRef(false);
  const pendingLoadRef = useRef(false);

  const recompactUnread = useCallback((items: Notification[]) => {
    setUnreadCount(items.filter((n) => !n.read).length);
  }, []);

  /** Merge an incoming notification, deduping on id and capping the list. */
  const ingest = useCallback(
    (incoming: Notification) => {
      setNotifications((prev) => {
        if (prev.some((n) => n.id === incoming.id)) {
          return prev;
        }
        const next = [incoming, ...prev].slice(0, MAX_ITEMS);
        recompactUnread(next);
        return next;
      });
      onNotificationRef.current?.(incoming);
    },
    [recompactUnread],
  );

  const load = useCallback(async () => {
    if (loadingRef.current) {
      // A fetch is already running; ask it to re-run once instead of piling on.
      pendingLoadRef.current = true;
      return;
    }
    loadingRef.current = true;
    try {
      do {
        pendingLoadRef.current = false;
        const inbox = await listNotifications(tokenRef.current, { take: MAX_ITEMS }, refreshRef.current);
        setNotifications(inbox.items);
        setUnreadCount(inbox.unreadCount);
      } while (pendingLoadRef.current);
    } catch {
      // Inbox fetch is best-effort; live pushes still populate the list.
    } finally {
      loadingRef.current = false;
    }
  }, []);

  const refresh = useCallback(() => {
    load().catch(() => {});
  }, [load]);

  // Initial inbox fetch.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Live connection. Subscribes to the process-wide shared hub rather than
  // building a per-instance connection: the add-in mounts its tree multiple
  // times (StrictMode + host bootstrap), and a per-effect socket leaves the
  // winning connection bound to an unmounted instance — frames then update dead
  // state while the visible instance has no socket. The shared hub fans every
  // frame out to all live subscribers, so this instance always receives them.
  useEffect(() => {
    setTokenProvider(async () => {
      const refreshed = await refreshRef.current?.();
      return refreshed ?? tokenRef.current ?? "";
    });

    return subscribeToHub(
      (n) => ingest(n),
      (isConnected) => {
        setConnected(isConnected);
        if (isConnected) {
          // On (re)connect, re-sync the inbox to catch anything missed while down.
          refresh();
        }
      },
    );
  }, [ingest, refresh]);

  const markAsRead = useCallback(async (id: number) => {
    // Optimistic; the server is the source of truth on next fetch.
    setNotifications((prev) => {
      const next = prev.map((n) => (n.id === id ? { ...n, read: true } : n));
      setUnreadCount(next.filter((n) => !n.read).length);
      return next;
    });
    try {
      await markRead(id, tokenRef.current, refreshRef.current);
    } catch {
      refresh(); // reconcile on failure
    }
  }, [refresh]);

  const markAllAsRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await markAllRead(tokenRef.current, refreshRef.current);
    } catch {
      refresh();
    }
  }, [refresh]);

  return { notifications, unreadCount, connected, markAsRead, markAllAsRead, refresh };
}
